import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SignIn } from "@/components/auth/SignIn";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { intentFromLanding } from "@/engine/state/entryIntent";
import { useHydrated } from "@/hooks/useHydrated";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>) => ({
    enter: typeof search.enter === "string" ? search.enter : undefined,
    room: typeof search.room === "string" ? search.room : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign in — SyncAni" },
      {
        name: "description",
        content:
          "Sign in to SyncAni with Google or an emailed link. Joining a room never needs an account.",
      },
      { property: "og:title", content: "Sign in — SyncAni" },
      {
        property: "og:description",
        content:
          "Sign in to SyncAni with Google or an emailed link. Joining a room never needs an account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthRoute,
});

function AuthRoute() {
  const { t } = useTranslation();
  // A client-side trip from the entry page already has the room intent in the
  // address; reading that address keeps the OAuth redirect independent of the
  // router's transient search state during the route transition.
  // Read once the page has hydrated: the server drew no intent, and a first
  // render that read one disagreed with it and was thrown away (#791).
  const hydrated = useHydrated();
  const intent = hydrated ? intentFromLanding(window.location.search) : null;
  // Two sign-ins, told apart by what the visitor was about to do (#708): one on
  // the way to lighting a fire, which only a host needs, and one that keeps
  // their character and rooms — which a visitor may always carry on without.
  const forCreating = intent?.kind === "create";

  const navigate = useNavigate();

  // The landing's topbar, and the ways in centred under it, as on the entry
  // screen.
  return (
    <main className="flex min-h-svh flex-col bg-background">
      {/* The way in is already drawn below, at full size, so the bar above it
          offers no second one. */}
      <LandingHeader showSignIn={false} />

      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <h1 className="mb-3 text-center text-xl font-semibold tracking-tight">
            {t(forCreating ? "account.createTitle" : "account.generalTitle")}
          </h1>
          <SignIn
            intent={intent}
            reason={t(
              forCreating ? "account.createReason" : "account.generalReason",
            )}
            onBack={() =>
              void navigate({
                to: "/",
                search: { enter: undefined, room: undefined },
              })
            }
            onSkip={
              forCreating
                ? undefined
                : () =>
                    void navigate({ to: "/home", search: { room: undefined } })
            }
          />
        </div>
      </div>
    </main>
  );
}
